import { createAsyncThunk } from '@reduxjs/toolkit';
import { IFilterObj } from 'src/components/filters/models/filter-response.model';
import { toFormFields2 } from 'src/lib';
import {
  createSku,
  updateSku,
  delSku,
  duplicateSku,
  fetchBulkDuplicatSkus,
  fetchSkuMetadata,
  overwriteSkuMetadata,
  fetchInnovationTableData,
  fetchDuplicateSkuPopupFilters,
  fetchDuplicateSkuTable,
  fetchSimulationResults,
  saveSimulatedInnovation,
  resetSimulationInnovation,
  updateOverwriteSku,
  fetchOverwriteSkuMetadata
} from '../api/innovation-api';
import { ISkuSelectedDetails } from '../types/sku-popup-model';

import {
  ITableData,
  TypeOfTableData
} from '../../pricing-simulator/types/generic-table.model';
import { filterColumnsForToggleView } from '../../pricing-simulator/utils/column-utils';

// eslint-disable-next-line import/prefer-default-export
export const fetchMetadata = createAsyncThunk(
  'fetchMetadata',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    filters: IFilterObj[];
  }) => {
    const response = await fetchSkuMetadata(
      args.sourceID,
      args.moduleID,
      args.scenarioID,
      args.filters
    );
    return {
      fields: toFormFields2(response.fields),
      combinations: response.combinations,
      sku_name_list: response.sku_name_list
    };
  }
);

export const overwriteMetadata = createAsyncThunk(
  'overwriteMetadata',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    filters: IFilterObj[];
  }) => {
    const response = await overwriteSkuMetadata(
      args.sourceID,
      args.moduleID,
      args.scenarioID,
      args.filters
    );
    return {
      fields: toFormFields2(response.fields)
      // combinations: response.combinations,
      // sku_name_list: response.sku_name_list
    };
  }
);

export const createNewSku = createAsyncThunk(
  'createNewSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    newskus: ISkuSelectedDetails[];
    filters: IFilterObj[];
  }) => {
    const response = await createSku({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      newSkus: args.newskus,
      filter_inputs: args.filters
    });
    return response;
  }
);

export const duplicateCurrSku = createAsyncThunk(
  'duplicateCurrSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
    duplicateCount: number;
    filters: IFilterObj[];
  }) => {
    const response = await duplicateSku({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      sku: args.skuName,
      duplicateCount: args.duplicateCount,
      filter_inputs: args.filters
    });
    return response;
  }
);

export const duplicateBulkSku = createAsyncThunk(
  'duplicateBulkSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    bulkSkus: {
      new_skus: object;
      existing_skus: object;
    };
    filters: IFilterObj[];
  }) => {
    const response = await fetchBulkDuplicatSkus({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      bulkSkus: args.bulkSkus,
      filter_inputs: args.filters
    });
    return response;
  }
);

export const editSku = createAsyncThunk(
  'editSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
    editedSkuObj: ISkuSelectedDetails[];
    filters: IFilterObj[];
  }) => {
    const response = await updateSku({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      sku: args.skuName,
      prevsku: args.skuName,
      editedSku: args.editedSkuObj,
      filter_inputs: args.filters
    });
    return response;
  }
);

export const fetchOverwriteMetadata = createAsyncThunk(
  'fetchOverwriteMetadata',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
  }) => {
    const response = await fetchOverwriteSkuMetadata(
      args.sourceID,
      args.moduleID,
      args.scenarioID,
      args.skuName
    );

    return response;
  }
);

export const createOverwriteSKU = createAsyncThunk(
  'createOverwriteSKU',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
    editedSkuObj: any[];
    filters: IFilterObj[];
  }) => {
    const response = await updateOverwriteSku({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      sku: args.skuName,
      prevsku: args.skuName,
      editedSku: args.editedSkuObj,
      filter_inputs: args.filters
    });
    return response;
  }
);

export const overwriteSku = createAsyncThunk(
  'overwriteSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
    editedSkuObj: ISkuSelectedDetails[];
    filters: IFilterObj[];
  }) => {
    return;
  }
);

export const deleteSku = createAsyncThunk(
  'deleteSku',
  async (args: {
    sourceID: number;
    moduleID: number;
    scenarioID: number;
    skuName: string;
    filters: IFilterObj[];
  }) => {
    const response = await delSku({
      sourceID: args.sourceID,
      moduleID: args.moduleID,
      scenarioID: args.scenarioID,
      sku: args.skuName,
      filter_inputs: args.filters
    });
    // return response;
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.ACTION,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols
    };
  }
);

export const getInnovationTableData = createAsyncThunk(
  'getInnovationTableData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    // change later to ITableData
    const response: any = await fetchInnovationTableData({
      sourceID: args.sourceId,
      moduleID: args.moduleId,
      scenarioID: args.scenarioId,
      filterInputs: args.filters
    });
    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.ACTION,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols
    };
  }
);

export const getDuplicateSkuPopupFilters = createAsyncThunk(
  'getDuplicateSkuPopupFilters',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response = await fetchDuplicateSkuPopupFilters({
      sourceID: args.sourceId,
      moduleID: args.moduleId,
      scenarioID: args.scenarioId,
      filterInputs: args.filters
    });
    return response;
  }
);

export const getDuplicateSkuTable = createAsyncThunk(
  'getDuplicateSkuTable',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
    duplicateFilters: IFilterObj[];
  }) => {
    const response = await fetchDuplicateSkuTable({
      sourceID: args.sourceId,
      moduleID: args.moduleId,
      scenarioID: args.scenarioId,
      filterInputs: args.filters,
      duplicateFilterInputs: args.duplicateFilters
    });
    return response;
  }
);

export const getSimulatedInnovationData = createAsyncThunk(
  'getSimulatedInnovationData',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response = await fetchSimulationResults({
      sourceID: args.sourceId,
      moduleID: args.moduleId,
      scenarioID: args.scenarioId,
      filterInputs: args.filters
    });

    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.ACTION,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols
    };
  }
);

export const getSaveSimulatedInnovation = createAsyncThunk(
  'getSaveSimulatedInnovation',
  async (args: { sourceID: number; moduleID: number; scenarioID: number }) => {
    const response = await saveSimulatedInnovation(
      args.sourceID,
      args.moduleID,
      args.scenarioID
    );
    return response;
  }
);

export const getResetSimulatedInnovation = createAsyncThunk(
  'getResetSimulatedInnovation',
  async (args: {
    sourceId: number;
    moduleId: number;
    scenarioId: number;
    filters: IFilterObj[];
  }) => {
    const response: any = await resetSimulationInnovation({
      sourceID: args.sourceId,
      moduleID: args.moduleId,
      scenarioID: args.scenarioId,
      filterInputs: args.filters
    });

    const resultCols = filterColumnsForToggleView(
      [TypeOfTableData.OUTPUT],
      response.header,
      ''
    );
    const inputCols = filterColumnsForToggleView(
      [
        TypeOfTableData.DISPLAY,
        TypeOfTableData.LEGEND_FLAG,
        TypeOfTableData.ACTION,
        TypeOfTableData.INPUT
      ],
      response.header,
      ''
    );
    return {
      response,
      resultCols,
      inputCols
    };
  }
);
